import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbUpIcon from '@atlaskit/icon/core/thumbs-up';
import { B300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const LikeIcon = () => <ThumbUpIcon label="" color={token('color.icon.subtlest')} />;

const ActiveLikeIcon = () => <ThumbUpIcon label="" color={token('color.icon.selected')} />;

const DislikeIcon = () => <ThumbsDownIcon label="" color={token('color.icon.subtlest')} />;

const ActiveDislikeIcon = () => (
	<ThumbsDownIcon label="" color={token('color.icon.selected', B300)} />
);

type AIFeedbackButtonsProps = {
	liked: boolean | undefined;
	setLiked: (value: boolean | undefined) => void;
	setShowDisplayFlag: (value: boolean) => void;
	onLikeButtonClickHandler?: (e: any) => void;
	onDislikeButtonClickHandler?: (e: any) => void;
	onDeselectLikeButtonClickHandler?: (e: any) => void;
	onDeselectDislikeButtonClickHandler?: (e: any) => void;
};

export const AIFeedbackButtons = ({
	liked,
	setLiked,
	setShowDisplayFlag,
	onLikeButtonClickHandler,
	onDislikeButtonClickHandler,
	onDeselectLikeButtonClickHandler,
	onDeselectDislikeButtonClickHandler,
}: AIFeedbackButtonsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	// Using a single state to represent both like/dislike states
	// true - liked, false - disliked, undefined - neither are selected
	const likeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			if (liked === undefined || liked === false) {
				setShowDisplayFlag(true);
				setLiked(true);
				onLikeButtonClickHandler && onLikeButtonClickHandler(e);
			} else {
				setLiked(undefined);
				onDeselectLikeButtonClickHandler && onDeselectLikeButtonClickHandler(e);
			}
		},
		[
			setShowDisplayFlag,
			setLiked,
			onLikeButtonClickHandler,
			liked,
			onDeselectLikeButtonClickHandler,
		],
	);

	const dislikeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			if (liked === undefined || liked === true) {
				setShowDisplayFlag(true);
				setLiked(false);
				onDislikeButtonClickHandler && onDislikeButtonClickHandler(e);
			} else {
				setLiked(undefined);
				onDeselectDislikeButtonClickHandler && onDeselectDislikeButtonClickHandler(e);
			}
		},
		[
			setShowDisplayFlag,
			setLiked,
			onDislikeButtonClickHandler,
			liked,
			onDeselectDislikeButtonClickHandler,
		],
	);

	return (
		<>
			<IconButton
				icon={liked === true ? ActiveLikeIcon : LikeIcon}
				isSelected={liked === true}
				onClick={likeButtonCallback}
				label={formatMessage(messages.ai_feedback_like_button_tooltip)}
				appearance={liked === true ? 'primary' : 'subtle'}
				isTooltipDisabled={false}
				spacing="compact"
				testId="ai-answer-like-button"
				shape="circle"
			/>
			<IconButton
				icon={liked === false ? ActiveDislikeIcon : DislikeIcon}
				isSelected={liked === false}
				onClick={dislikeButtonCallback}
				label={formatMessage(messages.ai_feedback_dislike_button_tooltip)}
				appearance={liked === false ? 'primary' : 'subtle'}
				isTooltipDisabled={false}
				spacing="compact"
				testId="ai-answer-dislike-button"
				shape="circle"
			/>
		</>
	);
};
